import React from 'react';
import styled from '@emotion/styled';
import Button from '../../components/common/Button';
import authService from '../../services/api/auth';
import {
  useRecaptchaToken,
  isCaptchaRequired,
  RECAPTCHA_SECURITY_ERROR,
} from '../../components/helpers/recaptchaUtils';
import { isValidEmail } from '../../components/helpers/guests/guestFieldValidation';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(8, 23, 42, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
  padding: 20px;
`;

const Modal = styled.div`
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 16px;
  padding: 32px 28px 36px;
  box-shadow: 0 25px 70px rgba(0, 0, 0, 0.35);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;

  h2 {
    margin: 0;
    font-size: 1.4rem;
    color: #0c2b4a;
  }

  button {
    border: none;
    background: transparent;
    font-size: 1.25rem;
    cursor: pointer;
    color: #4a5f78;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;

  input {
    width: 100%;
    padding: 14px 16px;
    border-radius: 10px;
    border: 1px solid #d9e1ec;
    font-size: 0.95rem;
    background-color: #f6f8fb;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &:focus {
      outline: none;
      border-color: #1d6fa3;
      box-shadow: 0 0 0 3px rgba(29, 111, 163, 0.12);
      background-color: #fff;
    }
  }

  .status {
    text-align: center;
    font-weight: 600;
    font-size: 0.95rem;
  }

  .status.error {
    color: #dc3545;
  }

  .status.success {
    color: #198754;
  }
`;

const ForgotPasswordModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [statusMessage, setStatusMessage] = React.useState('');
  const [statusType, setStatusType] = React.useState('success');
  const getCaptchaToken = useRecaptchaToken('reset_password');

  React.useEffect(() => {
    if (!isOpen) {
      setEmail('');
      setStatusMessage('');
      setStatusType('success');
      setLoading(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (event) => {
    event?.preventDefault();

    if (!isValidEmail(email, { required: true })) {
      setStatusType('error');
      setStatusMessage(
        email.trim() ? 'Ingresa un correo electrónico válido.' : 'Ingresa tu correo.'
      );
      return;
    }

    setLoading(true);
    setStatusMessage('');
    try {
      const captchaRequired = isCaptchaRequired();
      const captcha = await getCaptchaToken({ skip: !captchaRequired });
      if (captchaRequired && !captcha) {
        setStatusType('error');
        setStatusMessage(RECAPTCHA_SECURITY_ERROR);
        setLoading(false);
        return;
      }

      const response = await authService.requestPasswordReset({
        email: email.trim(),
        captcha,
      });
      setStatusType('success');
      setStatusMessage(response.data.message);
      setEmail('');
    } catch (error) {
      const message = error?.response?.data?.message || 'No se pudo procesar tu solicitud.';
      setStatusType('error');
      setStatusMessage(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(event) => event.stopPropagation()}>
        <Header>
          <h2>Recuperar contraseña</h2>
          <button type="button" onClick={onClose} aria-label="Cerrar modal">
            ×
          </button>
        </Header>

        <p>
          Ingresa tu correo electrónico y te enviaremos las instrucciones para restablecer tu
          contraseña.
        </p>

        <Form onSubmit={handleSubmit}>
          <input
            type="email"
            placeholder="Correo electrónico"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
          />

          {statusMessage && <span className={`status ${statusType}`}>{statusMessage}</span>}

          <Button
            text="Enviar instrucciones"
            disabled={loading}
            type="submit"
            style={{ width: '100%' }}
          />
        </Form>
      </Modal>
    </Overlay>
  );
};

export default ForgotPasswordModal;
